import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MdSearch, MdNotifications, MdSettings, MdPerson, MdLightMode, MdDarkMode, MdReceipt, MdPersonOutline, MdClose } from 'react-icons/md';
import { alertasAPI, clientesAPI, recibosAPI } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';

const HeaderContainer = styled.header`
  height: 70px;
  background-color: ${props => props.theme.colors.backgroundLight};
  box-shadow: ${props => props.theme.shadows.sm};
  border-bottom-right-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${props => props.theme.spacing.xl};
  position: sticky;
  top: 0;
  z-index: 50;
`;

const SearchContainer = styled.div`
  flex: 1;
  max-width: 500px;
  position: relative;
`;

const Name = styled.h2`
  font-size: 20px;
  margin: auto 0;
`

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  padding-left: 2.5rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  transition: ${props => props.theme.transitions.fast};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
  }

  &::placeholder {
    color: ${props => props.theme.colors.textLight};
  }
`;

const SearchIcon = styled(MdSearch)`
  position: absolute;
  left: ${props => props.theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.textLight};
  font-size: 1.25rem;
  pointer-events: none;
`;

const ClearButton = styled.button`
  position: absolute;
  right: ${props => props.theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.textLight};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => props.theme.borderRadius.sm};
  transition: ${props => props.theme.transitions.fast};

  &:hover {
    color: ${props => props.theme.colors.textPrimary};
    background-color: ${props => props.theme.colors.background};
  }

  svg {
    font-size: 1.25rem;
  }
`;

const SuggestionsContainer = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background-color: ${props => props.theme.colors.backgroundLight};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.lg};
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 4px;

    &:hover {
      background: ${props => props.theme.colors.textLight};
    }
  }
`;

const SuggestionSection = styled.div`
  padding: ${props => props.theme.spacing.sm} 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.colors.border};
  }
`;

const SectionTitle = styled.div`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SuggestionItem = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  transition: ${props => props.theme.transitions.fast};
  color: ${props => props.theme.colors.textPrimary};

  &:hover {
    background-color: ${props => props.theme.colors.background};
  }

  svg {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    flex-shrink: 0;
  }
`;

const SuggestionContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SuggestionTitle = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SuggestionSubtitle = styled.div`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textLight};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  color: ${props => props.theme.colors.textLight};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const LoadingState = styled.div`
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  color: ${props => props.theme.colors.textLight};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const IconButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.textSecondary};
  position: relative;
  transition: ${props => props.theme.transitions.fast};

  svg {
    font-size: 1.5rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.primary};
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background-color: ${props => props.theme.colors.danger};
  color: white;
  font-size: 10px;
  font-weight: bold;
  border-radius: 9px;
  border: 2px solid ${props => props.theme.colors.backgroundLight};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Header = () => {
  const navigate = useNavigate();
  const { themeName, toggleTheme, isDark } = useTheme();
  const [alertasNoLeidas, setAlertasNoLeidas] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchResults, setSearchResults] = useState({ clientes: [], recibos: [] });
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    fetchAlertasEstadisticas();
    // Actualizar cada 60 segundos
    const interval = setInterval(fetchAlertasEstadisticas, 60000);
    return () => clearInterval(interval);
  }, []);

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Buscar cuando cambia el término de búsqueda
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchTerm.trim().length >= 2) {
      setIsSearching(true);
      debounceRef.current = setTimeout(() => {
        performSearch(searchTerm);
      }, 300);
    } else {
      setSearchResults({ clientes: [], recibos: [] });
      setShowSuggestions(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm]);

  const fetchAlertasEstadisticas = async () => {
    try {
      const response = await alertasAPI.getEstadisticas();
      setAlertasNoLeidas(response.data.data?.no_leidas || 0);
    } catch (err) {
      console.error('Error al cargar estadísticas de alertas:', err);
    }
  };

  const performSearch = async (term) => {
    try {
      const [clientesResponse, recibosResponse] = await Promise.all([
        clientesAPI.getAll(),
        recibosAPI.getAll()
      ]);

      const clientes = clientesResponse.data.data || [];
      const recibos = recibosResponse.data.data || [];

      const searchLower = term.toLowerCase();

      // Filtrar clientes
      const clientesFiltered = clientes
        .filter(cliente => 
          cliente.nombre?.toLowerCase().includes(searchLower) ||
          cliente.rfc?.toLowerCase().includes(searchLower) ||
          cliente.email?.toLowerCase().includes(searchLower) ||
          cliente.telefono?.includes(searchLower)
        )
        .slice(0, 5);

      // Filtrar recibos
      const recibosFiltered = recibos
        .filter(recibo => 
          recibo.numero_recibo?.toString().includes(searchLower) ||
          recibo.cliente_nombre?.toLowerCase().includes(searchLower) ||
          recibo.concepto?.toLowerCase().includes(searchLower)
        )
        .slice(0, 5);

      setSearchResults({
        clientes: clientesFiltered,
        recibos: recibosFiltered
      });
      setShowSuggestions(true);
      setIsSearching(false);
    } catch (err) {
      console.error('Error al realizar búsqueda:', err);
      setIsSearching(false);
      setSearchResults({ clientes: [], recibos: [] });
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults({ clientes: [], recibos: [] });
    setShowSuggestions(false);
  };

  const handleNavigateToClient = (clienteId) => {
    navigate(`/clientes/${clienteId}`);
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const handleNavigateToReceipt = (reciboId) => {
    navigate(`/recibos/${reciboId}`);
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const hasResults = searchResults.clientes.length > 0 || searchResults.recibos.length > 0;

  return (
    <HeaderContainer>
      <Name>Aether Payment Manager</Name>
      <SearchContainer ref={searchRef}>
        <SearchIcon />
        <SearchInput 
          type="text" 
          placeholder="Buscar clientes, recibos..."
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={() => searchTerm.trim().length >= 2 && setShowSuggestions(true)}
        />
        {searchTerm && (
          <ClearButton onClick={handleClearSearch}>
            <MdClose />
          </ClearButton>
        )}
        
        {showSuggestions && (
          <SuggestionsContainer>
            {isSearching ? (
              <LoadingState>Buscando...</LoadingState>
            ) : hasResults ? (
              <>
                {searchResults.clientes.length > 0 && (
                  <SuggestionSection>
                    <SectionTitle>Clientes</SectionTitle>
                    {searchResults.clientes.map(cliente => (
                      <SuggestionItem
                        key={cliente.id}
                        onClick={() => handleNavigateToClient(cliente.id)}
                      >
                        <MdPersonOutline />
                        <SuggestionContent>
                          <SuggestionTitle>{cliente.nombre}</SuggestionTitle>
                          <SuggestionSubtitle>
                            {cliente.rfc && `RFC: ${cliente.rfc}`}
                            {cliente.rfc && cliente.telefono && ' • '}
                            {cliente.telefono && `Tel: ${cliente.telefono}`}
                            {(cliente.rfc || cliente.telefono) && cliente.saldo_pendiente > 0 && ' • '}
                            {cliente.saldo_pendiente > 0 && `Saldo: ${formatCurrency(cliente.saldo_pendiente)}`}
                          </SuggestionSubtitle>
                        </SuggestionContent>
                      </SuggestionItem>
                    ))}
                  </SuggestionSection>
                )}
                
                {searchResults.recibos.length > 0 && (
                  <SuggestionSection>
                    <SectionTitle>Recibos</SectionTitle>
                    {searchResults.recibos.map(recibo => (
                      <SuggestionItem
                        key={recibo.id}
                        onClick={() => handleNavigateToReceipt(recibo.id)}
                      >
                        <MdReceipt />
                        <SuggestionContent>
                          <SuggestionTitle>
                            Recibo #{recibo.numero_recibo} - {recibo.cliente_nombre}
                          </SuggestionTitle>
                          <SuggestionSubtitle>
                            {recibo.concepto && `${recibo.concepto} • `}
                            {formatCurrency(recibo.monto_total)}
                            {recibo.fecha_emision && ` • ${formatDate(recibo.fecha_emision)}`}
                          </SuggestionSubtitle>
                        </SuggestionContent>
                      </SuggestionItem>
                    ))}
                  </SuggestionSection>
                )}
              </>
            ) : (
              <EmptyState>
                No se encontraron resultados para "{searchTerm}"
              </EmptyState>
            )}
          </SuggestionsContainer>
        )}
      </SearchContainer>
      
      <Actions>
        <IconButton onClick={() => navigate('/alertas')} title="Ver alertas">
          <MdNotifications />
          {alertasNoLeidas > 0 && (
            <NotificationBadge>{alertasNoLeidas}</NotificationBadge>
          )}
        </IconButton>
  
        <IconButton 
          onClick={toggleTheme} 
          title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
        >
          {isDark ? <MdLightMode /> : <MdDarkMode />}
        </IconButton>
      </Actions>
    </HeaderContainer>
  );
};

export default Header;
